import { Injectable } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { template, templateSettings } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class DateTimeParser {
    constructor(public sitecore: Sitecore) {}

    /**
     * Determines the appropriate global message based on the difference between the provided date and the current date.
     *
     * @param  date - The target date to compare against the current date.
     * @returns - A message indicating whether the event has not started, starts now, or will start in a specific number of minutes.
     */
    getStartsAtTime(date: Date): string {
        const now = DateProviderService.getDate();
        const delta = Math.floor((date.getTime() - now.getTime()) / 60000);

        if (delta < 0) {
            return this.sitecore.globalMessages.NotStarted;
        }

        if (delta === 0) {
            return this.sitecore.globalMessages.StartsNow;
        }

        if (delta < 11) {
            templateSettings.interpolate = /{{([\s\S]+?)}}/g;
            const startingInMinutes = template(this.sitecore.globalMessages.StartingInMinutes);
            const startingInMinute = template(this.sitecore.globalMessages.StartingInMinute);

            const message = delta > 1 ? startingInMinutes({ delta }) : startingInMinute({ delta });

            return message;
        }

        return this.sitecore.globalMessages.NotStarted;
    }

    /**
     * Formats a given time in minutes into an "hours:minutes" string format.
     *
     * @param time - The total time in minutes.
     * @returns  - The formatted time in "hours:minutes" format.
     */
    getTime(time: number): string {
        const minutes = time % 60;
        const hours = (time - minutes) / 60;
        const formattedMinutes = minutes.toString().padStart(2, '0');

        return `${hours}:${formattedMinutes}`;
    }
}
